/**
 * ClickSend Sender ID panel.
 *
 * Lets the user configure who appears as the "from" on outbound CRM SMS.
 *
 * Two paths:
 *   - Alphanumeric brand (e.g. "Dan Pelling") — saved instantly. One-way only.
 *   - UK mobile (e.g. 07765940017) — kicked through ClickSend's OTP flow.
 *     Once verified, valid for 12 months. We surface a 30-day renewal banner.
 *
 * Implementation notes:
 *   - The CRM keeps `clicksend_sender_id` + `clicksend_sender_type` on the
 *     user's auth record so workspaces stay isolated.
 *   - The "pending" OTP state lives server-side too — the client never has
 *     to hold the `verification_id`. Makes the flow recoverable on refresh.
 */
import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Loader2, ShieldCheck, AlertTriangle, Phone, Hash, Trash2 } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const ALPHA_RE = /^[A-Za-z0-9]{1,11}$/;
const PHONE_LIKE = /^\+?\d[\d\s\-()]{8,}$/;

const detectSenderType = (value) => {
  const trimmed = (value || '').trim();
  const stripped = trimmed.replace(/[\s\-()]/g, '');
  if (PHONE_LIKE.test(trimmed) && /^\+?\d{10,15}$/.test(stripped)) return 'own_number';
  return 'alpha';
};

export default function ClickSendSenderPanel() {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [input, setInput] = useState('');
  const [otpStep, setOtpStep] = useState(false);
  const [otpCode, setOtpCode] = useState('');
  const [error, setError] = useState('');

  const token = () => localStorage.getItem('streakfree_token');

  const refresh = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/clicksend/status`, {
        headers: { 'Authorization': `Bearer ${token()}` },
      });
      if (res.ok) {
        const data = await res.json();
        setStatus(data);
        // If a pending verification is in flight server-side, jump into OTP step
        if (data.pending_verification_phone && !data.configured) {
          setOtpStep(true);
          setInput(data.pending_verification_phone);
        }
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { refresh(); }, []);

  const detected = detectSenderType(input);

  const handleSave = async () => {
    setError('');
    if (!input.trim()) {
      setError('Please enter a brand name or your mobile number.');
      return;
    }
    setBusy(true);
    try {
      if (detected === 'alpha') {
        if (!ALPHA_RE.test(input.trim())) {
          setError('Brand name must be 1–11 letters/digits, no spaces or symbols.');
          return;
        }
        const res = await fetch(`${API_URL}/api/clicksend/sender/save-alpha`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token()}` },
          body: JSON.stringify({ sender_id: input.trim() }),
        });
        const data = await res.json();
        if (!res.ok) {
          setError(data.detail || 'Could not save Sender ID.');
          return;
        }
        toast.success(`Sender ID "${data.sender_id}" saved.`);
        setInput('');
        await refresh();
      } else {
        // Phone number → kick off OTP
        const res = await fetch(`${API_URL}/api/clicksend/sender/request-otp`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token()}` },
          body: JSON.stringify({ phone_number: input.trim() }),
        });
        const data = await res.json();
        if (!res.ok) {
          setError(data.detail || 'Could not start verification.');
          return;
        }
        toast.success(`Code sent to ${data.phone_number}`);
        setOtpStep(true);
        await refresh();
      }
    } catch (e) {
      setError(e.message || 'Network error.');
    } finally {
      setBusy(false);
    }
  };

  const handleVerify = async () => {
    setError('');
    if (!otpCode.trim()) {
      setError('Enter the code you received by SMS.');
      return;
    }
    setBusy(true);
    try {
      const res = await fetch(`${API_URL}/api/clicksend/sender/verify-otp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token()}` },
        body: JSON.stringify({ otp_code: otpCode.trim() }),
      });
      const data = await res.json();
      if (!res.ok) {
        setError(data.detail || 'Verification failed — try again.');
        return;
      }
      toast.success(`Number ${data.sender_id} verified and saved.`);
      setOtpStep(false);
      setOtpCode('');
      setInput('');
      await refresh();
    } catch (e) {
      setError(e.message || 'Network error.');
    } finally {
      setBusy(false);
    }
  };

  const handleClear = async () => {
    if (!window.confirm('Remove the current Sender ID?')) return;
    setBusy(true);
    try {
      await fetch(`${API_URL}/api/clicksend/sender`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token()}` },
      });
      setOtpStep(false);
      setOtpCode('');
      setInput('');
      await refresh();
      toast.success('Sender ID cleared.');
    } finally {
      setBusy(false);
    }
  };

  if (loading) {
    return (
      <div className="p-4 bg-zinc-50 rounded-xl flex items-center gap-2 text-sm text-zinc-500">
        <Loader2 size={16} className="animate-spin" /> Loading SMS sender…
      </div>
    );
  }

  return (
    <div className="p-4 bg-zinc-50 rounded-xl space-y-4" data-testid="clicksend-panel">
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 bg-blue-100 rounded-xl flex items-center justify-center flex-shrink-0">
          <ShieldCheck size={20} className="text-blue-600" />
        </div>
        <div>
          <p className="font-medium text-zinc-900">SMS Sender ID</p>
          <p className="text-xs text-zinc-500">
            Who your texts appear from. SMS is built into StreakFree — no ClickSend account or setup needed, just choose a sender below.
          </p>
        </div>
      </div>

      {!status?.configured && !otpStep && (
        <div className="space-y-1.5" data-testid="clicksend-setup-steps">
          {[
            <>Type <strong>your mobile number</strong> below (recommended — customer replies come straight to your phone), or a <strong>brand name</strong> like <span className="font-mono">ShinyWindows</span> (max 11 letters/digits, no spaces — one-way only)</>,
            <>Mobile numbers get a <strong>6-digit code by text</strong> — type it in to prove it's yours</>,
            <>Done — every reminder and invoice text now comes from you</>,
          ].map((s, i) => (
            <div key={i} className="flex items-start gap-2.5">
              <span className="w-5 h-5 rounded-full bg-zinc-200 text-zinc-700 flex items-center justify-center text-[11px] font-bold flex-shrink-0 mt-0.5">{i + 1}</span>
              <span className="text-xs text-zinc-600 leading-relaxed">{s}</span>
            </div>
          ))}
        </div>
      )}

      {/* Current sender */}
      {status?.configured ? (
        <div className="p-3 bg-white border border-zinc-200 rounded-lg space-y-2" data-testid="clicksend-current">
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-2">
              {status.sender_type === 'own_number'
                ? <Phone size={14} className="text-emerald-600" />
                : <Hash size={14} className="text-purple-600" />}
              <span className="font-semibold text-zinc-900">{status.sender_id}</span>
              <span className="text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded-full bg-zinc-100 text-zinc-600">
                {status.sender_type === 'own_number' ? 'Mobile · 2-way' : 'Brand · one-way'}
              </span>
            </div>
            <button
              onClick={handleClear}
              disabled={busy}
              data-testid="clicksend-clear"
              className="text-xs text-rose-600 hover:text-rose-700 flex items-center gap-1"
            >
              <Trash2 size={12} /> Remove
            </button>
          </div>
          {status.sender_type === 'own_number' && status.expires_at && (
            <p className="text-xs text-zinc-500">
              Verified until {new Date(status.expires_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
              {typeof status.days_until_expiry === 'number' && ` · ${status.days_until_expiry} day${status.days_until_expiry === 1 ? '' : 's'} left`}
            </p>
          )}
          {status.renewal_warning && (
            <div className="flex items-center gap-2 p-2 bg-amber-50 border border-amber-200 rounded text-xs text-amber-800" data-testid="clicksend-renewal-warning">
              <AlertTriangle size={14} />
              Your verified number is within 30 days of expiry — re-verify to keep two-way SMS working.
            </div>
          )}
        </div>
      ) : (
        <p className="text-xs text-zinc-500">No sender set yet — texts can't send until you add one below (takes under a minute).</p>
      )}

      {/* OTP step */}
      {otpStep ? (
        <div className="space-y-2" data-testid="clicksend-otp-step">
          <label className="block text-xs text-zinc-500">Enter the 6-digit code we just texted to {status?.pending_verification_phone || input}</label>
          <div className="flex gap-2">
            <input
              value={otpCode}
              onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
              placeholder="123456"
              data-testid="clicksend-otp-input"
              className="flex-1 px-3 py-2 rounded-lg border border-zinc-300 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 outline-none text-sm font-mono tracking-widest text-center"
            />
            <button
              onClick={handleVerify}
              disabled={busy || otpCode.length < 4}
              data-testid="clicksend-otp-verify"
              className="px-4 py-2 rounded-lg bg-emerald-600 text-white text-sm font-medium hover:bg-emerald-700 disabled:opacity-50"
            >
              {busy ? <Loader2 size={14} className="animate-spin" /> : 'Verify'}
            </button>
            <button
              onClick={() => { setOtpStep(false); setOtpCode(''); }}
              disabled={busy}
              className="px-3 py-2 rounded-lg border border-zinc-300 text-sm text-zinc-700 hover:bg-zinc-50"
            >
              Cancel
            </button>
          </div>
        </div>
      ) : (
        <div className="space-y-2">
          <label className="block text-xs text-zinc-500">
            {status?.configured ? 'Change Sender ID' : 'Set Sender ID'}
            {' '}— brand name (max 11 chars) or UK mobile
          </label>
          <div className="flex gap-2">
            <input
              value={input}
              onChange={(e) => { setInput(e.target.value); setError(''); }}
              placeholder="07700900123  or  ShinyWindows"
              data-testid="clicksend-sender-input"
              className="flex-1 px-3 py-2 rounded-lg border border-zinc-300 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 outline-none text-sm"
            />
            <button
              onClick={handleSave}
              disabled={busy || !input.trim()}
              data-testid="clicksend-sender-save"
              className="px-4 py-2 rounded-lg bg-emerald-600 text-white text-sm font-medium hover:bg-emerald-700 disabled:opacity-50 flex items-center gap-1"
            >
              {busy && <Loader2 size={14} className="animate-spin" />}
              {detected === 'own_number' ? 'Send code' : 'Save'}
            </button>
          </div>
          {input && (
            <p className="text-[11px] text-zinc-500">
              Detected:{' '}
              <strong className="text-zinc-700">
                {detected === 'own_number' ? "UK mobile — we'll text you a 6-digit code to verify it's yours" : 'Brand name — saved instantly, but customers can\'t reply'}
              </strong>
            </p>
          )}
        </div>
      )}

      {error && (
        <div className="flex items-start gap-2 p-2 bg-rose-50 border border-rose-200 rounded text-xs text-rose-700" data-testid="clicksend-error">
          <AlertTriangle size={14} className="flex-shrink-0 mt-0.5" />
          <span>{error}</span>
        </div>
      )}
    </div>
  );
}
